import { currentTable, currentView, getDateFilter } from "../../../../app/viewManager.js";

const sumRowConfig = {
    entry:   ['Stunden'],
    timelog: ['Arbeitszeit (h)', 'Pausenzeit (h)'],
};

const tableNameMap = {
    time: 'Zeiterfassung',
    timelog: 'Arbeitstage',
    entry: 'Leistungsnachweise',
    project: 'Projekte',
    team: 'Teams',
    user: 'Benutzer',
    role: 'Rollen',
};

const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

function getTableDisplayName(key) {
    if (!key) return '';
    return tableNameMap[String(key).toLowerCase()] || key;
}

function formatIsoToGerman(isoDate) {
    if (!isoDate) return null;
    const parts = String(isoDate).split('-');
    return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : String(isoDate);
}

function sanitizeForFilename(name) {
    return String(name).replace(/[\\/:"*?<>|]+/g, '-').replace(/\s+/g, '-').replace(/\.+$/g, '');
}

function getFilterLabel() {
    if (typeof currentView === 'string' && currentView) {
        const dateFilter = getDateFilter(currentView);

        if (dateFilter) {
            if (dateFilter.mode === 'exact') {
                if (dateFilter.from && dateFilter.to) return `${formatIsoToGerman(dateFilter.from)}-${formatIsoToGerman(dateFilter.to)}`;
                if (dateFilter.from) return formatIsoToGerman(dateFilter.from);
                if (dateFilter.to) return formatIsoToGerman(dateFilter.to);
            }
            else if (dateFilter.mode === 'coarse') {
                const { year, months = [] } = dateFilter;
                const isYearValid = year && String(year) !== 'null';
                const pad = (num) => String(num).padStart(2, '0');

                if (months.length === 1) {
                    return isYearValid ? `${pad(months[0])}.${year}` : (monthNames[Number(months[0]) - 1] ?? months[0]);
                }

                if (isYearValid) return String(year);
            }
        }
    }

    if (currentTable && typeof currentTable.getHeaderFilters === 'function') {
        const headerFilters = currentTable.getHeaderFilters() || [];
        const dateColumnFilter = headerFilters.find(f => f.field === 'date' && f.value);
        if (dateColumnFilter?.value) return formatIsoToGerman(dateColumnFilter.value);

        const start = headerFilters.find(f => f.field === 'start' && f.value)?.value;
        const end = headerFilters.find(f => f.field === 'end' && f.value)?.value;
        
        if (start && end) return `${formatIsoToGerman(start)}-${formatIsoToGerman(end)}`;
        if (start) return formatIsoToGerman(start);
        if (end) return formatIsoToGerman(end);
    }

    //Fallback
    return 'Alle'; 
}

function getColumnIndex(cellRef) {
    const letters = cellRef.replace(/[0-9]/g, '');
    let index = 0;
    for (let i = 0; i < letters.length; i++) {
        index = index * 26 + (letters.charCodeAt(i) - 64);
    }
    return index - 1;
}

function hasSelectionCheckboxes(table) {
    return typeof table.getColumnDefinitions === 'function'
        && table.getColumnDefinitions().some(col => col.formatter === "rowSelection");
}

function getSelectedExportRows(table) {
    if (!table || !hasSelectionCheckboxes(table)) return [];
    return table.getSelectedRows();
}

export function getSelectedExportRowCount(table) {
    const activeTable = table || currentTable;
    if (!activeTable) return 0;

    return getSelectedExportRows(activeTable).length;
}

export function getExportRowIds(table) {
    const activeTable = table || currentTable;
    if (!activeTable) return [];

    const selectedRows = getSelectedExportRows(activeTable);
    if (selectedRows.length > 0) return selectedRows.map(row => row.getData().id);

    return activeTable.getData("active").map(row => row.id);
}

export function exportTable(table, filename) {
    const activeTable = table || currentTable;
    if (!activeTable) return;

    const now = new Date();
    const yyyy = now.getFullYear();
    const monthStr = String(now.getMonth() + 1).padStart(2, '0');
    const dayStr = String(now.getDate()).padStart(2, '0');
    const todayNumeric = `${yyyy}${monthStr}${dayStr}`;

    const displayName = getTableDisplayName(filename) || filename;
    const effectiveFilter = getFilterLabel();
    const exportFileName = `${sanitizeForFilename(displayName)}_${sanitizeForFilename(effectiveFilter)}_${todayNumeric}.xlsx`;

    const downloadRowRange = getSelectedExportRows(activeTable).length > 0 ? "selected" : "active";

    activeTable.download("xlsx", exportFileName, {
        sheetName: "Exportierte Daten",
        documentProcessing: function (workbook) {
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const colWidths = [];
            const targetColIndexes = new Set();

            for (let key in worksheet) {
                if (key.startsWith('!')) continue;
                const row = key.replace(/[A-Z]/g, '');
                
                if (row === '1') {
                    const val = worksheet[key].v;
                    if (val === "Berechtigung" || val === "Pausen") {
                        targetColIndexes.add(getColumnIndex(key));
                    }
                }
            }

            for (let key in worksheet) {
                if (key.startsWith('!')) continue;

                const colIndex = getColumnIndex(key);
                const row = key.replace(/[A-Z]/g, '');
                const cell = worksheet[key];
                let cellValue = cell.v;

                if (cell.t === 'd') {
                    cell.z = 'dd.mm.yyyy';
                    colWidths[colIndex] = Math.max(colWidths[colIndex] || 0, 10);
                    continue;
                }

                if (cellValue) {
                    if (targetColIndexes.has(colIndex) && row !== '1') {
                        cellValue = cellValue.toString().trim().replace(/\s+/g, ', ');
                        worksheet[key].v = cellValue;
                    }

                    const contentLength = cellValue.toString().length;
                    colWidths[colIndex] = Math.max(colWidths[colIndex] || 0, contentLength);
                }
            }

            worksheet['!cols'] = colWidths.map(maxChars => ({ wch: Math.max((maxChars || 10) + 3, 10) }));

            worksheet['!views'] = [{ state: 'frozen', ySplit: 1 }];

            const sumHeaders = sumRowConfig[String(filename || '').toLowerCase()];
            if (sumHeaders?.length) {
                const headerColMap = {};
                let firstColLetter = null;
                let firstColIndex = Infinity;

                for (let key in worksheet) {
                    if (key.startsWith('!')) continue;
                    const row = key.replace(/[A-Z]+/g, '');
                    if (row === '1') {
                        const col = key.replace(/[0-9]/g, '');
                        const idx = getColumnIndex(key);
                        if (sumHeaders.includes(worksheet[key].v)) headerColMap[worksheet[key].v] = col;
                        if (idx < firstColIndex) { firstColIndex = idx; firstColLetter = col; }
                    }
                }

                const refMatch = worksheet['!ref']?.match(/:[A-Z]+(\d+)$/);
                const lastDataRow = refMatch ? parseInt(refMatch[1], 10) : 1;
                const sumRow = lastDataRow + 1;

                for (const header of sumHeaders) {
                    const col = headerColMap[header];
                    if (!col) continue;

                    // Convert HH:MM strings to Excel time numbers (fraction of a day); decimal columns are left as plain numbers
                    let isTimeFormat = false;
                    for (let rowNum = 2; rowNum <= lastDataRow; rowNum++) {
                        const cell = worksheet[`${col}${rowNum}`];
                        if (cell?.v === undefined || cell?.v === null || cell?.v === '') continue;

                        if (typeof cell.v === 'string' && cell.v.includes(':')) {
                            const [hours, minutes] = cell.v.split(':').map(Number);
                            if (Number.isFinite(hours) && Number.isFinite(minutes)) {
                                worksheet[`${col}${rowNum}`] = { v: (hours * 60 + minutes) / 1440, t: 'n', z: '[h]:mm' };
                                isTimeFormat = true;
                            }
                        }
                    }

                    // SUM formula: elapsed-time format for HH:MM columns so hours can exceed 24, plain decimal for numeric columns
                    worksheet[`${col}${sumRow}`] = isTimeFormat
                        ? { f: `SUM(${col}2:${col}${lastDataRow})`, t: 'n', z: '[h]:mm' }
                        : { f: `SUM(${col}2:${col}${lastDataRow})`, t: 'n', z: '0.00' };
                }

                if (firstColLetter && !headerColMap[worksheet[`${firstColLetter}1`]?.v]) {
                    worksheet[`${firstColLetter}${sumRow}`] = { v: 'Gesamt:', t: 's' };
                }

                worksheet['!ref'] = worksheet['!ref'].replace(/([A-Z]+)\d+$/, `$1${sumRow}`);
            }

            return workbook;
        }
    }, downloadRowRange);
}